// src/contexts/ChatNotificationContext.tsx
// Global context for chat notification badges
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ChatNotificationContextType {
  unreadCount: number;
  hasUnread: boolean;
  setUnreadCount: (count: number) => void;
  incrementUnread: () => void;
  clearUnread: () => void;
}

const ChatNotificationContext = createContext<ChatNotificationContextType | null>(null);

export const useChatNotification = () => {
  const context = useContext(ChatNotificationContext);
  if (!context) {
    throw new Error('useChatNotification must be used within a ChatNotificationProvider');
  }
  return context;
};

interface ChatNotificationProviderProps {
  children: ReactNode;
}

export const ChatNotificationProvider: React.FC<ChatNotificationProviderProps> = ({ children }) => {
  const [unreadCount, setUnreadCountState] = useState(0);

  const setUnreadCount = useCallback((count: number) => {
    setUnreadCountState(count);
  }, []);

  const incrementUnread = useCallback(() => {
    setUnreadCountState(prev => prev + 1);
  }, []);

  const clearUnread = useCallback(() => {
    setUnreadCountState(0);
  }, []);

  return (
    <ChatNotificationContext.Provider
      value={{
        unreadCount,
        hasUnread: unreadCount > 0,
        setUnreadCount,
        incrementUnread,
        clearUnread,
      }}
    >
      {children}
    </ChatNotificationContext.Provider>
  );
};
